import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

export interface LiveTrackingLocation {
    latitude: number;
    longitude: number;
    timestamp?: string | number | Date;
    speed?: number;
    heading?: number;
    altitude?: number;
    accuracy?: number;
}

export interface LiveTrackingState {
    status: 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected' | 'auth-error';
    error?: string;
    vehicleKey?: string;
    sessionId?: string;
    serialNo?: string;
    locations: LiveTrackingLocation[];
    latest?: LiveTrackingLocation;
    tokenValidated?: boolean;
}

interface Options {
    url?: string; // base backend URL without namespace
    sessionId?: string; // optional, can be derived from token server-side
    token?: string; // share token (JWT) containing sessionId claim
    autoConnect?: boolean;
    maxPoints?: number;
}

// Hook to manage Socket.IO connection to /live-tracking namespace using share token + sessionId
export function useLiveTrackingSocket({ url, sessionId, token, autoConnect = true, maxPoints = 2000 }: Options) {
    const [state, setState] = useState<LiveTrackingState>({ status: 'idle', locations: [] });
    const socketRef = useRef<Socket | null>(null);
    const reconnectAttemptRef = useRef(0);

    const connect = useCallback(() => {
        // Allow connection when we have url + token; sessionId may be absent and resolved server-side
        if (!url || !token) {
            console.warn('useLiveTrackingSocket: Missing url or token', { url, token: !!token });
            return;
        }
        if (socketRef.current) return;

        console.log('useLiveTrackingSocket: Attempting connection', {
            url: `${url}/live-tracking`,
            hasToken: !!token,
            hasSessionId: !!sessionId
        });

        setState(s => ({ ...s, status: 'connecting', error: undefined }));

        const socket = io(`${url}/live-tracking`, {
            transports: ['websocket', 'polling'], // Allow fallback to polling
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            timeout: 20000, // 20 second timeout
            forceNew: true, // Force new connection
            auth: sessionId ? { token, sessionId } : { token },
            query: sessionId ? { token, sessionId } : { token },
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('useLiveTrackingSocket: Connected successfully');
            reconnectAttemptRef.current = 0;
            setState(s => ({ ...s, status: 'connected' }));
        });

        socket.on('disconnect', () => {
            console.log('useLiveTrackingSocket: Disconnected');
            setState(s => ({ ...s, status: 'disconnected' }));
        });

        socket.on('connect_error', (err: any) => {
            console.error('useLiveTrackingSocket: Connection error', err);
            reconnectAttemptRef.current += 1;
            setState(s => ({ ...s, status: 'error', error: err?.message || 'Connection error' }));
        });

        socket.on('auth-error', (payload: any) => {
            const errorCode = payload?.code || 'UNKNOWN';
            const errorMessage = payload?.message || 'Auth error';
            setState(s => ({
                ...s,
                status: 'auth-error',
                error: `${errorCode}: ${errorMessage}`
            }));
        });

        socket.on('connection-acknowledged', (payload: any) => {
            setState(s => ({
                ...s,
                status: 'connected',
                sessionId: payload.sessionId || sessionId || s.sessionId,
                tokenValidated: payload.tokenValidated,
            }));
        });

        socket.on('subscription-success', (payload: any) => {
            setState(s => ({
                ...s,
                vehicleKey: payload.vehicleKey,
                serialNo: payload.serialNo,
                sessionId: payload.sessionId || s.sessionId,
            }));
        });

        socket.on('auto-subscribe-success', (payload: any) => {
            setState(s => ({
                ...s,
                vehicleKey: payload.vehicleKey,
                sessionId: payload.sessionId || s.sessionId,
            }));
        });

        socket.on('auto-subscribe-error', (payload: any) => {
            setState(s => ({
                ...s,
                error: payload?.message || 'Auto subscription failed',
            }));
        });

        socket.on('location-update', (payload: any) => {
            const loc: LiveTrackingLocation = {
                latitude: payload?.location?.latitude,
                longitude: payload?.location?.longitude,
                timestamp: payload?.location?.timestamp || Date.now(),
                speed: payload?.location?.speed,
                heading: payload?.location?.heading,
                altitude: payload?.location?.altitude,
                accuracy: payload?.location?.accuracy,
            };
            setState(s => {
                const lastLoc = s.locations[s.locations.length - 1];
                if (!lastLoc || (lastLoc.timestamp && loc.timestamp && loc.timestamp > lastLoc.timestamp)) {
                    const locations = [...s.locations, loc];
                    if (locations.length > maxPoints) locations.splice(0, locations.length - maxPoints);
                    return { ...s, locations, latest: loc };
                }
                return s;
            });
        });
    }, [url, sessionId, token, maxPoints]);

    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (autoConnect && url && token) connect();
        return () => disconnect();
    }, [autoConnect, url, sessionId, token, connect, disconnect]);

    return { ...state, connect, disconnect };
}
